import FeedbackRegenerationAction from 'components/Grading/FeedbackRegenerationAction';
import FeedbackRenderer from 'components/Grading/FeedbackRenderer';
import Header from 'components/Grading/Header';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import { QuizReviewContext } from 'components/QuizGrading/QuizReviewContext';
import ResponseViewer from 'components/QuizGrading/ResponseViewer';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Container, Dropdown, IconButton, Stack } from 'shell/components';

const QuizGradingLayout = ({ device, ...props }) => {
    const { t } = useTranslation('assignment');
    const {
        currentAssignment: quiz,
        currentSubmission,
        form,
        setForm,
        tab,
        setTab,
        meta,
        selectedCriterionId,
        setSelectedCriterionId,
        gradeOverrides,
        setGradeOverrides,
        selectedFeedbackId,
        setSelectedFeedbackId,
        feedbackRegenerationReasons,
        feedbackRegenerationNeededItemIds,
        feedbackUpdatedItemIds,
        pendingFeedbackItemIds,
        handleClickRegenerateFeedback,
        handleSelectDropdown,
        handleRevertFeedback,
        handleCriterionNavigation,
        selectedFileId,
        setSelectedFileId,
        highlights,
        setShowFailedDialog,
    } = props;
    const isDesktop = device === 'desktop';
    const isTemplated =
        quiz.ai_feedback_settings?.structure === 'templated' ||
        currentSubmission.ai?.feedback?.feedback_type === 'templated';
    const feedbackProps = {
        assignment: quiz,
        currentSubmission: currentSubmission.ai?.feedback
            ? currentSubmission
            : {
                  ...currentSubmission,
                  ai: { ...currentSubmission.ai, feedback: form.feedback },
              },
        selectedFileId,
        setSelectedFileId,
        locations: highlights,
        form,
        setForm,
        selectedFeedbackId,
        setSelectedFeedbackId,
        pendingFeedbackItemIds,
        feedbackRegenerationNeededItemIds,
        feedbackUpdatedItemIds,
        onRevertFeedback: handleRevertFeedback,
        handleCriterionNavigation,
    };
    useEffect(() => setSelectedFileId(''), [selectedCriterionId, selectedFeedbackId, tab, setSelectedFileId]);

    return (
        <QuizReviewContext.Provider
            value={{ questions: quiz.questions, selectedCriterionId, onSelectQuestion: setSelectedFileId }}
        >
            <Container
                maxWidth={false}
                sx={(theme) => ({
                    position: 'absolute',
                    top: 0,
                    zIndex: theme.zIndex.drawer + 1,
                    p: 0,
                    pt: device === 'mobile' ? '109px' : '65px',
                    height: '100%',
                    minHeight: 0,
                    background: theme.palette.ensightful.neutral[100],
                })}
            >
                <Header />
                <Stack direction={isDesktop ? 'row' : 'column'} sx={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
                    <Stack
                        sx={{
                            width: isDesktop ? '50%' : '100%',
                            minHeight: isDesktop ? 0 : '40%',
                            flexShrink: 0,
                            overflow: isDesktop ? 'auto' : 'visible',
                        }}
                    >
                        <ResponseViewer
                            key={currentSubmission.id}
                            quiz={quiz}
                            submission={currentSubmission}
                            selectedCriterionId={selectedCriterionId}
                            selectedFeedbackId={selectedFeedbackId}
                            feedback={form.feedback}
                            tab={tab}
                            selectedQuestionId={selectedFileId}
                        />
                    </Stack>
                    <Stack
                        id={'actions-container'}
                        sx={(theme) => ({
                            width: isDesktop ? '50%' : '100%',
                            flex: 1,
                            minHeight: 0,
                            overflow: isDesktop ? 'auto' : 'visible',
                            borderLeft: isDesktop ? `1px solid ${theme.palette.ensightful.neutral[300]}` : undefined,
                            background: theme.palette.ensightful.shades[0],
                        })}
                    >
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ p: 5 }}>
                            <ButtonGroup
                                maxWidth={'60%'}
                                options={[
                                    { key: 'grades', value: t('common:ui.grades', 'Grades') },
                                    {
                                        key: 'feedback',
                                        value: t('common:ui.feedback', 'Feedback'),
                                        badge: !meta.hasSeenFeedback,
                                        disabled: !quiz.ai_feedback_enabled || !form.feedback,
                                    },
                                ]}
                                selected={tab}
                                onChange={setTab}
                            />
                            {quiz.ai_feedback_enabled && !isTemplated && (
                                <Stack direction={'row'} spacing={3}>
                                    <FeedbackRegenerationAction
                                        aria-label={t(
                                            'grading-section.actions.regenerate-feedback',
                                            'Regenerate Feedback'
                                        )}
                                        count={Object.keys(feedbackRegenerationReasons).length}
                                        disabled={!form.feedback}
                                        onClick={handleClickRegenerateFeedback}
                                    />
                                    {tab === 'feedback' && (
                                        <Dropdown
                                            id={'quiz-grading-more-options'}
                                            options={[
                                                {
                                                    key: 'copy-all',
                                                    value: t(
                                                        'grading-section.actions.copy-all-feedback',
                                                        'Copy all feedback'
                                                    ),
                                                    icon: 'copy',
                                                },
                                                {
                                                    key: 'delete',
                                                    value: t(
                                                        'grading-section.actions.delete-all-feedback',
                                                        'Delete all feedback'
                                                    ),
                                                    icon: 'delete',
                                                },
                                            ]}
                                            rootComponent={
                                                <IconButton
                                                    kind={'moreHorizontal'}
                                                    size={'medium'}
                                                    variant={'square'}
                                                    aria-label={t('common:controls.more-options', 'More options')}
                                                />
                                            }
                                            onSelect={handleSelectDropdown}
                                        />
                                    )}
                                </Stack>
                            )}
                        </Stack>
                        {tab === 'grades' && (
                            <RubricRenderer
                                assignment={quiz}
                                currentSubmission={currentSubmission}
                                form={form}
                                setForm={setForm}
                                gradeOverrides={gradeOverrides}
                                setGradeOverrides={setGradeOverrides}
                                selectedCriterionId={selectedCriterionId}
                                setSelectedCriterionId={setSelectedCriterionId}
                                selectedFileId={selectedFileId}
                                setSelectedFileId={setSelectedFileId}
                                locations={highlights}
                                feedbackRegenerationReasons={feedbackRegenerationReasons}
                                handleCriterionNavigation={handleCriterionNavigation}
                                setShowFailedDialog={setShowFailedDialog}
                            />
                        )}
                        {tab === 'feedback' &&
                            (typeof currentSubmission.ai?.feedback === 'string' ? (
                                <LegacyFeedbackRenderer
                                    {...feedbackProps}
                                    setShowFailedDialog={setShowFailedDialog}
                                    needsFeedbackRegeneration={feedbackRegenerationNeededItemIds.length > 0}
                                />
                            ) : (
                                <FeedbackRenderer {...feedbackProps} />
                            ))}
                    </Stack>
                </Stack>
            </Container>
        </QuizReviewContext.Provider>
    );
};

export default QuizGradingLayout;
